import { check } from "@pro/common/utils";
import { AFormat } from "../contracts/atomicassets";

const PACK_SCHEMA_FORMAT: readonly Readonly<AFormat>[] = [
	{name: "name", type: "string"},
	{name: "img", type: "image"},
	{name: "cards_quantity", type: "uint8"},
	{name: "rarity_amount", type: "uint8[]"},
	{name: "rarity_percent", type: "float[]"},
	{name: "items_schema", type: "string"},
	{name: "description", type: "string"},
];

const ASSET_SCHEMA_FORMAT: readonly Readonly<AFormat>[] = [
	{name: "name", type: "string"},
	{name: "img", type: "image"},
	{name: "edition", type: "uint8"},
	{name: "rarity", type: "string"},
	{name: "description", type: "string"},
];

const HERO_SCHEMA_FORMAT: readonly Readonly<AFormat>[] = [
	{name: "name", type: "string"},
	{name: "img", type: "image"},
	{name: "edition", type: "uint8"},
	{name: "rarity", type: "string"},
	{name: "description", type: "string"},
	{name: "tribe", type: "string"},
];

export interface ITemplateJson
{
	/* table fields */
	readonly template_id: number,
	readonly schema_name: TSchemaName,
	readonly transferable: boolean,
	readonly burnable: boolean,
	readonly max_supply: number,
	readonly description: string,
	readonly hidden?: boolean,

	/* sheet data */
	readonly id: string,

	/* serialized attributes */
	readonly name: string,
	readonly img: string,
}

export interface IPackJson extends ITemplateJson
{
	/* serialized attributes */
	readonly cards_quantity: number,
	readonly rarity_amount: readonly number[],
	readonly rarity_percent: readonly number[],
	readonly items_schema: TSchemaName,
}

export interface IAssetJson extends ITemplateJson
{
	/* serialized attributes */
	readonly edition: number,
	readonly rarity: string,
	readonly unpack: boolean,
}

export interface IHeroJson extends IAssetJson
{
	/* serialized attributes */
	readonly tribe: string,
}

export type TRarity = "common" | "rare" | "unique" | "epic" | "legendary" | "mythic";
export type TSchemaName = "packs" | "books" | "symbols" | "heroes";

export class AssetConf
{
	static readonly COLLECTION_DATA = {
		name: "Tribal Books",
		description: "Tribal Books assets are playable, blockchain-based collectibles for use in the upcoming free-to-play online step-by-step NFT-card game. In the game, players compete with each other in separate rounds using the cards and magical forces from the books. Players can trade Tribal Books assets (cards and books) to build their collections, as well as use them for playing after the game will be released.",
		url: "https://tribalbooks.io",
	};

	static readonly RARITIES: readonly string[] = [
		"common",
		"rare",
		"unique",
		"epic",
		"legendary",
		"mythic",
	];

	static readonly SCHEMA_NAMES: readonly TSchemaName[] = [
		"packs",
		"books",
		"symbols",
		"heroes"
	];
	static readonly PACK_SCHEMA_FORMAT = PACK_SCHEMA_FORMAT;
	static readonly ASSET_SCHEMA_FORMAT = ASSET_SCHEMA_FORMAT;
	static readonly HERO_SCHEMA_FORMAT = HERO_SCHEMA_FORMAT;
	static TEST_COLLECTION = "collection12";

	readonly _byAttribId: ReadonlyMap<string, ITemplateJson>;
	readonly _byTemplateId: ReadonlyMap<number, ITemplateJson>;
	readonly _bySchema: ReadonlyMap<TSchemaName, ITemplateJson[]>;

	constructor(data: readonly ITemplateJson[])
	{
		let byAttribId = new Map<string, ITemplateJson>();
		let byTemplateId = new Map<number, ITemplateJson>();
		let bySchema = new Map<TSchemaName, ITemplateJson[]>();

		AssetConf.SCHEMA_NAMES.forEach(s => bySchema.set(s, []));

		for (let item of data) {
			check(!byAttribId.has(item.id), `"id" is not unique: ${JSON.stringify(item)}`);
			byAttribId.set(item.id, item);
			byTemplateId.set(item.template_id, item);

			let list = bySchema.get(item.schema_name);
			check(list, `invalid schema name: "${item.schema_name}"`);
			list.push(item);
		}

		this._byAttribId = byAttribId;
		this._byTemplateId = byTemplateId;
		this._bySchema = bySchema;
	}

	findByAttribId<T extends ITemplateJson>(id: string): T | undefined
	{
		return this._byAttribId.get(id) as T;
	}

	findByTemplateId<T extends ITemplateJson>(templateId: number): T | undefined
	{
		return this._byTemplateId.get(templateId) as T;
	}

	getByTemplateId<T extends ITemplateJson>(templateId: number): T
	{
		let template = this._byTemplateId.get(templateId) as T;
		if (!template)
			throw Error("Template not found");
		return template;
	}

	getBySchema<T extends ITemplateJson>(schema: TSchemaName): readonly T[]
	{
		let list = this._bySchema.get(schema);
		check(list, `invalid schema name: "${schema}"`);
		return list as T[];
	}

	get packs(): readonly IPackJson[]
	{
		return this.getBySchema("packs");
	}

	get books(): readonly IAssetJson[]
	{
		return this.getBySchema("books");
	}

	get symbols(): readonly IAssetJson[]
	{
		return this.getBySchema("symbols");
	}
	get heroes(): readonly IAssetJson[]
	{
		return this.getBySchema("heroes");
	}
}
